<template>
  <div :class="{ disabled }" class="sf-upload">
    <div
      :class="{
        active: uploadActive || model,
        invalid: showError && !disabled,
        dragging,
      }"
      class="sf-upload__area"
      @drop.prevent="onDrop"
      @dragover.prevent
      @click="injectType">
      <template v-if="model && !downloaded">
        <template v-if="!multiple">
          <div
            :style="{ 'background-image': `url('${type === 'base64' ? model : preview}')`}"
            class="sf-upload__image">
            <slot v-if="!preview && type !== 'base64'" name="icon"/>
          </div>
          <div class="sf-upload__text">
            <div class="sf-upload__progress">
              <transition name="progress-bar" appear>
                <span v-if="model" class="sf-upload__progress__status"/>
              </transition>
            </div>
            <transition name="upload-success" appear>
              <div v-if="showError" class="sf-xmark sf-upload__error"/>
              <div v-else class="sf-checkmark sf-upload__success"/>
            </transition>
            <div class="sf-upload__delete" @click.stop="deleteImg">{{ staticText.delete }}</div>
          </div>
        </template>
        <template v-else>
          <div class="sf-upload__image">
            <slot v-if="type === 'file'" name="icon"/>
          </div>
          <div class="sf-upload__text multiple">
            <div class="sf-upload__progress">
              <div class="sf-upload__progress__text sf-ta-l">
                {{ model.name }}
              </div>
            </div>
            <div v-if="showError" class="sf-xmark sf-upload__error" @click.stop="deleteImg"/>
            <div v-else class="sf-upload__delete" @click.stop="deleteImg"/>
          </div>
        </template>
      </template>
      <template v-else-if="downloaded">
        <div
          :style="{ 'background-image': `url('${downloaded}')`}"
          class="sf-upload__image"/>
        <div class="sf-upload__text">
          <div class="sf-upload__delete" @click.stop="deleteImg">{{ staticText.delete }}</div>
        </div>
      </template>
      <template v-else>
        <div class="sf-upload__image">
          <slot name="icon"/>
        </div>
        <div class="sf-upload__text">
          <slot name="text"/>
        </div>
      </template>
    </div>
    <div
      v-qa="errorElement.selector"
      :class="{ active: showError }"
      class="sf-error"
      v-text="errorElement.message"/>
    <transition name="v-fade">
      <div v-if="optionPopup || cameraPopup" class="sf-upload__options" @click.self="stop">
        <div class="sf-upload__options__inner">
          <div v-if="$slots.info" class="sf-upload__options__tooltip">
            <h3 class="sf-upload__options__title">
              <slot name="info-title"/>
            </h3>
            <div v-show="cameraPopup" class="sf-upload__webcam">
              <video ref="video" class="sf-upload__video" autoplay/>
              <sf-button class="secondary sm" @click="capture">{{ staticText.capture }}</sf-button>
            </div>
            <div v-if="!cameraPopup">
              <slot name="info"/>
            </div>
            <div class="sf-upload__buttons">
              <sf-button class="ternary lg sf-mt block" @click="document">
                <folders-ic slot="left"/>
                {{ staticText.storageUpload }}
              </sf-button>
              <sf-button v-if="hasWebcam" class="ternary lg sf-mt block" @click="webcam">
                <camera-ic slot="left"/>
                {{ staticText.webcam }}
              </sf-button>
            </div>
            <div v-if="!backButton" class="sf-upload__close" @click="stop"/>
            <sf-button v-else class="lg block sf-mt-20 transparent text-light" @click="stop">
              {{ staticText.return }}
            </sf-button>
          </div>
        </div>
      </div>
    </transition>
    <input
      ref="uploadDocument"
      class="sf-upload__input"
      type="file"
      :accept="nativeAccept"
      :capture="nativeCapture"
      @change="formatDocument">
  </div>
</template>

<script>
import cameraIc from '../../img/camera-ic.svg';
import foldersIc from '../../img/folders-ic.svg';
import Field from './field.vue';

const CAPTURE_OPTIONS = {
  USER: 'user',
  ENVIRONMENT: 'environment',
};

export default {
  components: {
    cameraIc,
    foldersIc,
  },
  extends: Field,
  props: {
    width: {
      type: [Number, String],
      default: 480,
    },
    height: {
      type: [Number, String],
      default: 360,
    },
    staticText: {
      type: Object,
      default: () => {
        return {
          capture: 'Capture',
          delete: 'DELETE',
          storageUpload: 'From disk',
          webcam: 'Use webcam',
          return: 'back',
        };
      },
    },
    type: {
      type: String,
      default: 'base64',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    memory: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    backButton: {
      type: Boolean,
      default: false,
    },
    mobileAccept: {
      type: String,
      default: null,
    },
    mobileCapture: {
      type: String,
      default: null,
      validate(value) {
        return Object.values(CAPTURE_OPTIONS).includes(value.toLowerCase());
      },
    },
  },
  data() {
    return {
      model: this.$attrs.value || '',
      preview: '',
      downloaded: this.$attrs.value,
      optionPopup: false,
      cameraPopup: false,
      hasWebcam: true,
      invalid: false,
      video: {},
      devices: false,
      dragging: false,
      isPhone: false,
    };
  },
  computed: {
    uploadActive() {
      return !!this.model;
    },
    nativeAccept() {
      return this.isPhone && this.mobileAccept ? this.mobileAccept : false;
    },
    nativeCapture() {
      return this.isPhone && this.mobileCapture ? this.mobileCapture : false;
    },
  },
  watch: {
    async model(value) {
      this.errorCode = '';
      this.$emit('input', value);
      await this.$nextTick();
    },
  },
  mounted() {
    window.document.addEventListener('dragover', this.watchDrag);
    window.document.addEventListener('dragleave', this.stopDrag);
    window.document.addEventListener('dragend', this.stopDrag);

    if (navigator.mediaDevices) {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        if (devices.some(c => c.kind === 'videoinput')) this.devices = true;
      });
    }
    this.checkIsPhone();
  },
  beforeDestroy() {
    window.document.removeEventListener('dragover', this.watchDrag);
    window.document.removeEventListener('dragleave', this.stopDrag);
    window.document.removeEventListener('dragend', this.stopDrag);
  },
  methods: {
    checkIsPhone() {
      this.isPhone = /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone/i.test(
        navigator.userAgent
      );
    },
    injectType() {
      if (this.model || this.disabled || this.downloaded) return;

      if (this.isPhone || !navigator.mediaDevices || !this.devices) {
        this.hasWebcam = false;
      }

      // Do not show info popup if webcam is disabled and there is no info
      if (!this.$slots.info) {
        this.document();
      } else {
        this.optionPopup = true;
      }
    },
    webcam() {
      this.optionPopup = false;
      this.cameraPopup = true;
      this.video = this.$refs.video;
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices.getUserMedia({ video: true }).then(stream => {
          this.video.srcObject = stream;
          this.video.play();
        });
      }
    },
    document() {
      if (this.$refs.uploadDocument) {
        this.$refs.uploadDocument.click();
      }
    },
    async capture() {
      const canvas = document.createElement('canvas');
      canvas.width = this.width;
      canvas.height = this.height;
      canvas.getContext('2d').drawImage(this.video, 0, 0, this.width, this.height);
      if (this.type === 'base64') {
        this.model = canvas.toDataURL();
      } else {
        const blob = await this.getImageAsBlob({ canvas });
        this.model = new File([blob], 'document.png', { type: 'image/png' });
        this.preview = canvas.toDataURL();
      }
      this.stop();
    },
    formatDocument(event) {
      const input = event.target;
      const reader = new FileReader();

      if (input.files && input.files[0]) {
        if (this.type === 'base64') {
          reader.onload = e => (this.model = e.target.result);
        } else {
          this.model = input.files[0];
          const isFileTypeImage =
            this.model && this.model.type && this.model.type.split('/')[0] === 'image';

          reader.onload = e => (this.preview = isFileTypeImage ? e.target.result : '');
        }

        reader.readAsDataURL(input.files[0]);
        this.stop();
      }
    },
    stop() {
      this.cameraPopup = false;
      this.optionPopup = false;
      if (this.video !== null && this.video.srcObject) {
        const stream = this.video.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach(track => {
          track.stop();
        });
      }
    },
    deleteImg() {
      this.model = '';
      this.$refs.uploadDocument.value = '';
      this.downloaded = false;
    },
    invalidate(errorCode) {
      this.pristine = false;
      this.errorCode = errorCode || 'validationError';
    },
    watchDrag($event) {
      this.dragging = $event.dataTransfer.types.includes('Files');
    },
    stopDrag() {
      this.dragging = false;
    },
    onDrop(event) {
      const { files } = event.dataTransfer;
      this.formatDocument({ target: { files } });
      this.stopDrag();
    },
    getImageAsBlob({ canvas, mime = 'image/png', quality = 0.8 }) {
      return new Promise(resolve => {
        canvas.toBlob(resolve, mime, quality);
      });
    },
  },
};
</script>
